import { AfterViewInit, Component, OnInit, effect, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable, of } from 'rxjs';
import { AdvancedGridviewComponent } from 'src/app/controls/advanced-gridview/advanced-gridview.component';
import { AdvancedDataSource } from 'src/app/controls/advanced-gridview/datasources/advanced-datasource';
import { AdvancedGridviewModel } from 'src/app/controls/advanced-gridview/models/advanced-gridview-model';
import { ColumnDefinition } from 'src/app/controls/advanced-gridview/models/column-definition';
import { ColumnType } from 'src/app/controls/advanced-gridview/models/column-type';
import { MenuITemAction } from 'src/app/controls/advanced-gridview/models/menu-item-action';
import { MenuItemEvent } from 'src/app/controls/advanced-gridview/models/menu-item-event';
import { SortInfos } from 'src/app/controls/advanced-gridview/models/sort-infos';
import { FormMode } from 'src/app/controls/common/form-mode';
import { FilterType } from 'src/app/controls/filters/models/filter-type';
import { DefaultListComponent } from 'src/app/controls/list/default-list/default-list.component';
import { EnumHelper } from 'src/app/helpers/enum-helper';
import { RestApiEventConfiguration } from 'src/app/models/RestApiCaller/configuration/rest-api-event-configuration';
import { Rights } from 'src/app/models/enums/rights';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { ApiConfigurationState } from '../models/api-configuration-state';
import { EventService } from './services/event-service';

@Component({
    selector: 'app-api-configuration-event-list',
    imports: [AdvancedGridviewComponent],
    templateUrl: './api-configuration-event-list.component.html',
    styleUrl: './api-configuration-event-list.component.scss',
    providers: [EventService]
})
export class ApiConfigurationEventListComponent extends DefaultListComponent<RestApiEventConfiguration> implements OnInit, AfterViewInit {
    /**
     * Etat de la configuration d'API
     */
    public readonly apiConfigurationState = inject(ApiConfigurationState);

    /**
     * Liste des enums utilisés dans le template.
     */
    public FormMode = FormMode;

    /**
     * Valeur indiquant si le composant est en lecture seule.
     */
    public readOnly: boolean;

    /**
     * Configuration des colonnes de la liste.
     */
    tableConfig: ColumnDefinition[] = null;

    /**
     * Constructeur
     * @param dialog : Service pour ouvrir des boites de dialogue.
     * @param memoryService : Service pour récupérer les informations en mémoire.
     * @param enumHelper : Classe utilitaire pour les énumérations.
     */
    constructor(
        public override dialog: MatDialog,
        protected override memoryService: MemoryService,
        public enumHelper: EnumHelper,
        private spinner: NgxSpinnerService,
        private eventService: EventService,
        private notificationService: NotificationService
    ) {
        super(dialog, memoryService);

        if (this.memoryService.getConnectedUser().params.idVisible === true) {
            this.tableConfig = [
                new ColumnDefinition('id', 'Id', ColumnType.Numeric, FilterType.Text, 1, 5, true),
                new ColumnDefinition('name', 'Nom', ColumnType.Text, FilterType.Text, 2, 40, true),
                new ColumnDefinition('description', 'Description', ColumnType.Text, FilterType.Text, 3, 50, true),
            ];
        } else {
            this.tableConfig = [
                new ColumnDefinition('name', 'Nom', ColumnType.Text, FilterType.Text, 2, 40, true),
                new ColumnDefinition('description', 'Description', ColumnType.Text, FilterType.Text, 3, 55, true),
            ];
        }

        // Nouvelle instance des éléments du menu pour supprimer l'élement consulter.
        this.menuItems = [];
        this.AddMenuItem('Dupliquer', MenuITemAction.Duplicate, Rights.ProcessCreate);
        this.AddMenuItem('Editer', MenuITemAction.Update, Rights.ProcessEdit);
        this.AddMenuItem('Supprimer', MenuITemAction.Delete, Rights.ProcessDelete);

        // S'abonne aux évènements de raffraichissement de la datasource pour raffraichir la liste.
        this.eventService.needRefresh.subscribe(() => {
            this.refresh();
        });

        effect(() => {
            if (this.apiConfigurationState.$mode() === FormMode.Reading) {
                this.readOnly = true;
            } else {
                this.readOnly = false;
            }
        });
    }

    /**
     * Initialise le composant.
     */
    ngOnInit(): void {
        this.dataSource = new AdvancedDataSource(null, this.eventService, this.spinner, this.notificationService);
        this.filteredTableModel = new AdvancedGridviewModel(this.tableConfig, new SortInfos('name', 'asc'));
    }

    /**
     * Appellé après l'initialisation de la vue du composant.
     */
    ngAfterViewInit(): void {
        this.gridView.getSortableItemsFunction = this.getSortablesItems.bind(this);
    }

    /**
     * Appellée lorsque l'utilisateur clique sur un élément du menu contextuel.
     * @param event : événement retourné par le menu contextuel.
     */
    public onAction(event: MenuItemEvent<RestApiEventConfiguration>): void {
        if (event.action == MenuITemAction.Read) {
            // Cas de la consultation.
            this.itemDisplayed.emit(event.context);
        } else if (event.action == MenuITemAction.Update) {
            // Cas de la mise à jour.
            this.itemEdited.emit(event.context);
        } else if (event.action == MenuITemAction.Duplicate) {
            // Cas de duplication.
            this.itemDuplicated.emit(event.context);
        } else if (event.action == MenuITemAction.Delete) {
            // Cas de la suppression --> affichage de la boite de dialogue de confirmation.
            this.displayDeleteConfirmationDialog(`Vous êtes sur le point de supprimer l'évènement '${event.context.name}'.`, event.context);
        }
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton creation.
     */
    public onCreation(): void {
        this.itemCreated.emit();
    }

    public onItemsSorted(items: RestApiEventConfiguration[]) {
        this.eventService.eventsList = items;
    }

    protected getSortablesItems(item: RestApiEventConfiguration): Observable<RestApiEventConfiguration[]> {
        return of(
            this.dataSource.datas.sort((a, b) => {
                return a.order < b.order ? -1 : 1;
            })
        );
    }
}
